import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Loader2,
  Trophy,
  Users,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Zap,
  ChevronRight,
  ChevronDown,
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useAdminLeagues, useAdminLeagueConfigs, useAdminMemberCounts, useAdminLeagueOwners } from '@/hooks/useAdminData'
import { formatAuditAction } from '@/lib/audit-format'
import { relativeTime } from '@/lib/time-format'

type HealthStatus = 'ok' | 'warn' | 'error' | 'none'

function HealthDot({ status }: { status: HealthStatus }) {
  const cls: Record<HealthStatus, string> = {
    ok: 'bg-emerald-500',
    warn: 'bg-amber-500',
    error: 'bg-red-500',
    none: 'bg-zinc-300 dark:bg-zinc-600',
  }
  return <span className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ${cls[status]}`} />
}

function StatusIcon({ status }: { status: HealthStatus }) {
  switch (status) {
    case 'ok': return <CheckCircle2 className="h-4 w-4 text-emerald-500" />
    case 'warn': return <AlertTriangle className="h-4 w-4 text-amber-500" />
    case 'error': return <XCircle className="h-4 w-4 text-red-500" />
    default: return <Clock className="h-4 w-4 text-zinc-400" />
  }
}

export default function AdminDashboard() {
  const navigate = useNavigate()

  const { data: leagues = [], isLoading: isLoadingLeagues } = useAdminLeagues()
  const { data: leagueConfigs = [] } = useAdminLeagueConfigs()
  const { data: memberCounts = [] } = useAdminMemberCounts()
  const { data: owners = [] } = useAdminLeagueOwners()

  const { data: lastSync } = useQuery({
    queryKey: ['admin-dash-sync'],
    queryFn: async () => {
      const { data } = await supabase
        .from('sync_logs')
        .select('created_at, errors')
        .eq('sync_type', 'fixtures')
        .order('created_at', { ascending: false })
        .limit(1)
      return data?.[0] ?? null
    },
    refetchInterval: 60000,
  })

  const { data: recentActivity = [] } = useQuery({
    queryKey: ['admin-dash-activity'],
    queryFn: async () => {
      const { data } = await supabase
        .from('audit_logs')
        .select('id, action, user_id, league_id, entity_type, details, created_at')
        .order('created_at', { ascending: false })
        .limit(15)
      return data ?? []
    },
  })

  const { data: memberNames = [] } = useQuery({
    queryKey: ['admin-member-names'],
    queryFn: async () => {
      const { data } = await supabase
        .from('league_memberships')
        .select('user_id, league_id, display_name')
        .eq('status', 'active')
      return data ?? []
    },
  })

  const { data: allPipeline = [] } = useQuery({
    queryKey: ['admin-dash-pipeline'],
    queryFn: async () => {
      const { data } = await supabase
        .from('pipeline_status')
        .select('league_id, matchday, season, fixtures_synced_at, selections_scored_at, results_email_sent_at, autopicks_processed_at, errors')
      return data ?? []
    },
    refetchInterval: 60000,
  })

  const [pipelineExpanded, setPipelineExpanded] = useState(false)

  const totalLeagues = leagues.length
  const totalMembers = memberCounts.reduce((sum, m) => sum + m.count, 0)

  const paidLeagues = leagues.filter(l => (l.settings as Record<string, unknown> | null)?.payment_status === 'paid')
  const waivedLeagues = leagues.filter(l => {
    const status = (l.settings as Record<string, unknown> | null)?.payment_status
    return !status || status === 'waived'
  })
  const totalRevenue = paidLeagues.reduce((sum, l) => {
    const cost = ((l.settings as Record<string, unknown> | null)?.season_registration_fee as number) ?? 0
    return sum + cost
  }, 0)

  const syncAge = lastSync ? Date.now() - new Date(lastSync.created_at).getTime() : null
  const syncStatus: HealthStatus = !syncAge ? 'none'
    : syncAge < 30 * 60000 ? 'ok'
    : syncAge < 3 * 3600000 ? 'warn'
    : 'error'

  const sevenDaysAgo = Date.now() - 7 * 24 * 3600000
  const pipelineWithIssues = allPipeline.filter(p => {
    const syncTime = p.fixtures_synced_at ? new Date(p.fixtures_synced_at).getTime() : 0
    if (syncTime < sevenDaysAgo) return false
    const hasErrors = p.errors && (Array.isArray(p.errors) ? p.errors.length > 0 : Object.keys(p.errors as Record<string, unknown>).length > 0)
    return (p.fixtures_synced_at && !p.selections_scored_at) || hasErrors
  })
  const pipelineStatus: HealthStatus = pipelineWithIssues.length > 0 ? 'warn' : allPipeline.length > 0 ? 'ok' : 'none'

  if (isLoadingLeagues) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-8">
      <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">Dashboard</h1>

      {/* Stats cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
          <div className="flex items-center gap-2 text-zinc-500 dark:text-zinc-400 text-xs font-medium mb-2">
            <Trophy className="h-3.5 w-3.5" /> LEAGUES
          </div>
          <p className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">{totalLeagues}</p>
        </div>
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
          <div className="flex items-center gap-2 text-zinc-500 dark:text-zinc-400 text-xs font-medium mb-2">
            <Users className="h-3.5 w-3.5" /> TOTAL MEMBERS
          </div>
          <p className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">{totalMembers}</p>
        </div>
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
          <div className="flex items-center gap-2 text-zinc-500 dark:text-zinc-400 text-xs font-medium mb-2">
            <DollarSign className="h-3.5 w-3.5" /> REVENUE
          </div>
          <p className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">${totalRevenue.toFixed(0)}</p>
          <p className="text-xs text-zinc-400 mt-0.5">
            {paidLeagues.length} paid{waivedLeagues.length > 0 ? ` · ${waivedLeagues.length} waived` : ''}
          </p>
        </div>
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
          <div className="flex items-center gap-2 text-zinc-500 dark:text-zinc-400 text-xs font-medium mb-2">
            <AlertCircle className="h-3.5 w-3.5" /> WAIVED
          </div>
          <p className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">{waivedLeagues.length}</p>
          <p className="text-xs text-zinc-400 mt-0.5">
            {waivedLeagues.length === totalLeagues ? 'All leagues waived' : `of ${totalLeagues} leagues`}
          </p>
        </div>
      </div>

      {/* System health */}
      <div className="flex items-center gap-6 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-5 py-3">
        <div className="flex items-center gap-2.5">
          <HealthDot status={syncStatus} />
          <div>
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Auto-Sync</p>
            <p className="text-xs text-zinc-500">{lastSync ? `Last run ${relativeTime(lastSync.created_at)}` : 'No data'}</p>
          </div>
        </div>
        <div className="h-8 w-px bg-zinc-200 dark:bg-zinc-700" />
        <button
          onClick={() => pipelineWithIssues.length > 0 && setPipelineExpanded(prev => !prev)}
          className={`flex items-center gap-2.5 ${pipelineWithIssues.length > 0 ? 'cursor-pointer hover:opacity-80' : ''}`}
        >
          <StatusIcon status={pipelineStatus} />
          <div className="text-left">
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Pipeline</p>
            <p className="text-xs text-zinc-500">
              {pipelineWithIssues.length > 0
                ? `${pipelineWithIssues.length} issue(s)`
                : allPipeline.length > 0 ? 'Healthy' : 'No data'}
            </p>
          </div>
          {pipelineWithIssues.length > 0 && (
            <ChevronDown className={`h-3.5 w-3.5 text-zinc-400 transition-transform ${pipelineExpanded ? 'rotate-180' : ''}`} />
          )}
        </button>
        <div className="h-8 w-px bg-zinc-200 dark:bg-zinc-700" />
        <div className="flex items-center gap-1.5">
          <Zap className="h-3.5 w-3.5 text-zinc-400" />
          <span className="text-xs text-zinc-500">Every 15 min</span>
        </div>
      </div>

      {pipelineExpanded && pipelineWithIssues.length > 0 && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-5 space-y-3">
          <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300">Pipeline Issues</h3>
          <div className="space-y-2">
            {pipelineWithIssues.map((p, i) => {
              const league = leagues.find(l => l.id === p.league_id)
              const leagueName = league?.name ?? p.league_id
              const rawErrors = p.errors as unknown
              const errorStrings: string[] = Array.isArray(rawErrors)
                ? rawErrors.map(e => typeof e === 'string' ? e : typeof e === 'object' && e !== null ? (e as Record<string, unknown>).message as string ?? JSON.stringify(e) : String(e))
                : []
              const hasErrors = errorStrings.length > 0
              const unscored = p.fixtures_synced_at && !p.selections_scored_at
              const syncedAt = p.fixtures_synced_at ? relativeTime(p.fixtures_synced_at) : null
              return (
                <button
                  key={i}
                  onClick={() => navigate(`/admin/leagues/${p.league_id}/tools`)}
                  className="w-full text-left flex items-start justify-between gap-3 rounded-lg bg-white dark:bg-zinc-900 border border-amber-100 dark:border-amber-900 px-4 py-3
                             hover:border-amber-300 dark:hover:border-amber-700 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{leagueName}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">MD {p.matchday} · {p.season}{syncedAt ? ` · Synced ${syncedAt}` : ''}</p>
                    {unscored && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Fixtures synced but not scored</p>}
                    {hasErrors && errorStrings.map((err, j) => (
                      <p key={j} className="text-xs text-red-600 dark:text-red-400 mt-1">{err}</p>
                    ))}
                  </div>
                  <ChevronRight className="h-4 w-4 text-zinc-400 shrink-0 mt-0.5" />
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        {/* Leagues overview */}
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
          <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100 dark:border-zinc-800">
            <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Leagues</h2>
            <button
              onClick={() => navigate('/admin/leagues')}
              className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-0.5"
            >
              View all <ChevronRight className="h-3 w-3" />
            </button>
          </div>
          <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {leagues.slice(0, 10).map((league) => {
              const config = leagueConfigs.find(c => c.league_id === league.id)
              const mc = memberCounts.find(m => m.league_id === league.id)
              const owner = owners.find(o => o.league_id === league.id)
              return (
                <button
                  key={league.id}
                  onClick={() => navigate(`/admin/leagues/${league.id}`)}
                  className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{league.name}</p>
                    <p className="text-xs text-zinc-500 truncate">
                      {config?.competition_name ?? 'Unknown'} · {league.season} · {owner?.display_name ?? '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <span className="text-xs text-zinc-500">{mc?.count ?? 0} members</span>
                    <ChevronRight className="h-4 w-4 text-zinc-300 dark:text-zinc-600" />
                  </div>
                </button>
              )
            })}
            {leagues.length === 0 && (
              <p className="px-5 py-8 text-sm text-zinc-400 text-center">No leagues yet</p>
            )}
          </div>
        </div>

        {/* Recent activity */}
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
          <div className="px-5 py-3 border-b border-zinc-100 dark:border-zinc-800">
            <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Recent Activity</h2>
          </div>
          <div className="divide-y divide-zinc-100 dark:divide-zinc-800 max-h-[400px] overflow-y-auto">
            {recentActivity.map((log) => {
              const userName = memberNames.find(m => m.user_id === log.user_id && m.league_id === log.league_id)?.display_name
              const leagueName = leagues.find(l => l.id === log.league_id)?.name
              return (
              <div key={log.id} className="px-5 py-2.5">
                <p className="text-sm text-zinc-700 dark:text-zinc-300">{formatAuditAction(log.action, log.details as Record<string, unknown> | null, { userName, leagueName })}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{leagueName ? `${leagueName} · ` : ''}{relativeTime(log.created_at)}</p>
              </div>
              )
            })}
            {recentActivity.length === 0 && (
              <p className="px-5 py-8 text-sm text-zinc-400 text-center">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

